import { Medication } from "./MedicationTypes";





interface BackgroundMetadata {
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

// Common types used across categories
export interface MultiSelectResponse {
  name: string;
  description?: string;
}

export interface ResponseDetail {
  response: boolean;
  description?: string;
}

export interface Provider {
  providerName: string;
  credentials?: string;
  practicingCity: string;
  careStartDate: Date;
  careEndDate?: Date;
}

export interface SpecificMedicalProblem extends ResponseDetail {
  type?: 'Medical' | 'Surgical';
}

export interface FamilyCondition {
  relationship: string;
  age?: number;
  deceased: string;
  description?: string;
}

export interface SleepQuestionnaire {
  date: Date;
  sleepBehaviors: string[];
  averageHoursSlept: string;
  averageMinutesToDoze: string;
  personalSleepHistory: {
    drowsyDay: boolean;
    hallucinationDreams: boolean;
    sleepAttacks: boolean;
    cataplecticAttacks: boolean;
    sleepParalysis: boolean;
    legRestlessness: boolean;
  };
}

// Category-specific data structures
export interface ProviderHistoryData extends BackgroundMetadata {
  primaryCareProvider: Provider;
  hasReferringProvider: boolean;
  isSameAsPrimaryCare?: boolean;
  referringProvider?: Provider;
}

export interface MedicationsData extends BackgroundMetadata {
  takingMedications: boolean | null;
  activeMedications: Medication[];
  allergicToMedications: boolean | null;
  medicationAllergies: string[];
}

export interface PastMedicalHistoryData extends BackgroundMetadata {
  hasPaceMaker: boolean;
  generalMedicalProblems: MultiSelectResponse[];
  specificMedicalProblems: SpecificMedicalProblem[];
}

export interface SocialHistoryData extends BackgroundMetadata {
  maritalStatus: 'Married' | 'Single' | 'Widowed' | 'Divorced' | 'Other';
  socialStatuses: MultiSelectResponse[];
}

export interface FamilyHistoryData extends BackgroundMetadata {
  badSleep: ResponseDetail;
  familyMedicalConditions: FamilyCondition[];
}

export const epworthOptions = ["None", "Slight", "Moderate", "High"] as const;
export type EpworthResponseType = typeof epworthOptions[number];

export interface SleepMedicineHistoryData extends BackgroundMetadata {
  pastSleepStudy: ResponseDetail & {
    document?: File | string;
  };
  isOnPAP: boolean;
  papVendor?: string;
  drowsinessAccident: ResponseDetail;
  sleepQuestionnaire: {
    averageHoursSlept: string;
    averageMinutesToDoze: string;
    numberOfAwakenings: string;
    sleepBehaviors: string[];
    personalSleepHistory: string[];
    averageBedtime: string;
    averageRiseTime: string;
    sleepStatus?: string;
  };
  epworthSleepinessScale: {
    reading: EpworthResponseType | null;
    watchingTV: EpworthResponseType | null;
    inactivePublic: EpworthResponseType | null;
    passenger: EpworthResponseType | null;
    afternoonLayDown: EpworthResponseType | null;
    talking: EpworthResponseType | null;
    afterLunch: EpworthResponseType | null;
    inTraffic: EpworthResponseType | null;
    score: number;
  };
}

export interface WeightHealthData extends BackgroundMetadata {
  botheredByWeight: string;
  discussWeight: boolean;
  weightLossMethods?: string[];
  eatingDisorder?: boolean;
  pregnant?: boolean;
  pregnantPlanning?: boolean;
}

export interface ReviewOfSystemsData extends BackgroundMetadata {
  unit: 'Imperial' | 'Metric';
  height: {
    feet?: string;
    inches?: string;
    cm?: string;
  };
  weight: {
    pounds?: string;
    kg?: string;
  };
  recentSymptoms: string[];
  accidentRelated?: {
    date: string;
    accidentInsurance?: string;
    employer?: string;
    accident: 'Automobile Accident' | 'Work Accident' | 'No';
  };
}

export interface WeightHistoryData extends BackgroundMetadata {
  weightGainStart: string;
  weightGainInfluence: string[];
  weightTrend: string;
  goalWeight?: string;
  goalWeightUnit?: 'lbs' | 'kg';
  weightGoalDescription: MultiSelectResponse[];
}

export interface MedicationWithSideEffects extends MultiSelectResponse {
  sideEffects: string;
}

export interface WeightTreatmentHistoryData extends BackgroundMetadata {
  medications: MedicationWithSideEffects[];
  lifestylePrograms: MultiSelectResponse[];
  priorSurgeriesOrProcedures: string[];
  logging: MultiSelectResponse[];
}

export interface Exercise extends BackgroundMetadata {
  type: string;
  frequency: string;
  intensity: string;
  device: string;
  limitations: string;
}
export interface ExerciseHistoryData extends BackgroundMetadata {
  exercises: Exercise[];
}

export interface EatingHabitsData extends BackgroundMetadata {
  mealsAndSnacks: string;
  eatingBehaviors: MultiSelectResponse[];
  foodAndDrink: string;
  eatingDisorders: MultiSelectResponse[];
}

export interface MentalHealthHistoryData extends BackgroundMetadata {
  mentalHealthConcern: MultiSelectResponse[];
  motivationAndReadiness: string;
} 

export interface OtherWeightManagementHistoryData extends BackgroundMetadata {
  medicalHistory: string[];
  type2Diabetes: boolean;
  diabetesMedications?: MultiSelectResponse[];
  checkBloodSugar?: boolean | null;
  eyeDisease?: ResponseDetail;
  gastrointestinalProblems: MultiSelectResponse[];
  stomachOrBowelSurgery: ResponseDetail;
  upcomingSurgeries: ResponseDetail;
  femaleHistory: string[];
}

export interface MedicationFeedbackData extends BackgroundMetadata {
  medicationFeedback: string;
  medicationSymptoms: MultiSelectResponse[];
}

export interface WeightLossProgressData extends BackgroundMetadata {
  weightLossProgress: string;
  changesInSleep: ResponseDetail;
  additionalComments: string;
}

export interface MedicalHistoryForm {
  providerHistory: ProviderHistoryData;
  medications: MedicationsData;
  pastMedicalHistory: PastMedicalHistoryData;
  socialHistory: SocialHistoryData;
  familyHistory: FamilyHistoryData;
  sleepMedicineHistory: SleepMedicineHistoryData;
  weightHealth: WeightHealthData;
  reviewOfSystems: ReviewOfSystemsData;
}

// *** DEPRECATED ***
export interface MedicalHistory {
  userId: string;
  type: "New Patient" | "Follow-up";
  appointmentId?: string;
  therapyId?: string;
  form: MedicalHistoryForm,
}

// Union type of all possible category data types
export type PaperworkCategoryData =
  | ProviderHistoryData
  | MedicationsData
  | PastMedicalHistoryData
  | SocialHistoryData
  | FamilyHistoryData
  | SleepMedicineHistoryData
  | WeightHealthData
  | ReviewOfSystemsData;

export type WeightManagementCategoryData =
  | WeightHistoryData
  | WeightTreatmentHistoryData
  | ExerciseHistoryData
  | EatingHabitsData
  | MentalHealthHistoryData
  | OtherWeightManagementHistoryData
  | MedicationFeedbackData
  | WeightLossProgressData;

export type CategoryData =
  | PaperworkCategoryData
  | WeightManagementCategoryData;

// Category type enum
export type PaperworkCategoryType =
  | 'providerHistory'
  | 'medications'
  | 'pastMedicalHistory'
  | 'socialHistory'
  | 'familyHistory'
  | 'sleepMedicineHistory'
  | 'weightHealth'
  | 'reviewOfSystems';

export type WeightManagementCategoryType =
  | 'weightHistory'
  | 'weightTreatmentHistory'
  | 'exerciseHistory'
  | 'eatingHabits'
  | 'mentalHealthHistory'
  | 'otherWeightManagementHistory'
  | 'medicationFeedback'
  | 'weightLossProgress';

export type WMFollowUpCategoryType =
  | 'reviewOfSystems'
  | 'medications'
  | 'medicationFeedback'
  | 'exerciseHistory'
  | 'weightLossProgress';


export type CategoryType = 
  | PaperworkCategoryType
  | WeightManagementCategoryType;

export interface PatientForm {
  id: string;
  patientId: string;
  appointmentId?: string;
  type: string;
  progress: string;
  categories: string[];
  formData: Record<string, any>;
  createdBy: string;
  updatedBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export const FormTypes = {
  NEW_PATIENT: "newPatient",
  FOLLOW_UP: "followUp",
  DETAILS: "details",
  WEIGHT_MANAGEMENT: "weightManagement",
  WM_FOLLOW_UP: "wmFollowUp",
} as const;

export type FormType = typeof FormTypes[keyof typeof FormTypes];